import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, ShieldCheck, KeyRound, CheckCircle2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const PortalOnboarding = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing setup token"); return; }
    axios.get(`${API}/onboarding/verify/${token}`)
      .then(r => setInfo(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Invalid or expired token"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await axios.post(`${API}/onboarding/complete`, { token, new_password: pw1 });
      setDone(true);
      toast.success("Account activated!");
      setTimeout(() => nav("/portal/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Activation failed");
    }
    setBusy(false);
  };

  return (
    <Layout>
      <SEOHead title="Activate Your AuditIQ Account" path="/portal/onboarding" />
      <section className="max-w-[560px] mx-auto px-6 py-14">
        <div className="glass p-8" data-testid="onboarding-panel">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-gold">
            <ShieldCheck size={14} /> One-Time Setup
          </div>

          {error && (
            <div className="mt-6 p-4 rounded bg-red-500/10 border border-red-500/40 text-red-300 text-sm"
                 data-testid="onboarding-error">
              {error}
            </div>
          )}

          {!error && !info && (
            <div className="mt-8 flex items-center gap-2 text-cream-dim">
              <Loader2 size={14} className="animate-spin" /> Verifying your setup link…
            </div>
          )}

          {!error && info && !done && (
            <>
              <h1 className="font-display text-3xl text-cream mt-3">Welcome, {info.name || "subscriber"}.</h1>
              <p className="text-sm text-cream-dim mt-2">
                Your <span className="text-gold font-mono">{info.email}</span> account is ready.
                {info.wix_plan_name && (
                  <> Plan: <span className="text-gold">{info.wix_plan_name}</span>.</>
                )}{" "}
                Set a password to activate it.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="onboarding-form">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-steel">New password</label>
                  <Input type="password" required value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
                    data-testid="onboarding-pw1" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Confirm password</label>
                  <Input type="password" required value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
                    data-testid="onboarding-pw2" />
                </div>
                <Button disabled={busy} className="btn-gold h-11 w-full"
                        data-testid="onboarding-submit">
                  {busy ? <><Loader2 size={14} className="animate-spin mr-2" />Activating…</>
                        : <><KeyRound size={14} className="mr-2" />Activate Account</>}
                </Button>
              </form>
            </>
          )}

          {done && (
            <div className="mt-8 text-center" data-testid="onboarding-success">
              <CheckCircle2 size={48} className="text-[color:var(--auditiq-success,#5bbf85)] mx-auto" />
              <h2 className="font-display text-2xl text-cream mt-4">Account activated</h2>
              <p className="text-sm text-cream-dim mt-2">Redirecting you to sign in…</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default PortalOnboarding;
